// The Subscribe page's promo-campaign decisions (#6965), with no Angular in them.
// See design-docs/promo-campaigns.md.
import { PaddleEventData } from '@paddle/paddle-js';
import { CheckoutPromo } from '@cf-shared/store-interface';
import { PromoCodeResult } from '@cf-shared/data-model/promo-campaign';

/** Which message the page shows about the promo code. */
export enum PromoNotice {
  /** The green "code applied" box. */
  Applied = 'applied',
  /** "This code is not valid." */
  Unknown = 'unknown',
  /** "This code has expired": the code was given after the campaign ended. */
  Expired = 'expired',
  /** "The sale has ended": the page was open with the sale on it when the campaign ended. */
  Ended = 'ended',
}

/**
 * The message for the server's answer. `endedWhileOpen` is set when Paddle refused
 * the discount at checkout and the page asked the server again: the buyer was looking
 * at the sale price, so "the sale has ended" says what happened better than "this
 * code has expired". No code, no message.
 */
export function promoNotice(promo: CheckoutPromo | undefined, endedWhileOpen: boolean): PromoNotice | undefined {
  if (!promo) return undefined;
  switch (promo.result) {
    case PromoCodeResult.Applied: return PromoNotice.Applied;
    case PromoCodeResult.Unknown: return PromoNotice.Unknown;
    case PromoCodeResult.Expired: return endedWhileOpen ? PromoNotice.Ended : PromoNotice.Expired;
  }
}

/**
 * The `promo_code` to stamp into a checkout's custom data (#6965), or undefined. Only
 * a plan that carries the campaign's discount is credited to the campaign, so a buyer
 * who applied a code but bought a plan outside the sale is not counted for it.
 */
export function promoCodeForCheckout(planHasDiscount: boolean, promo: CheckoutPromo | undefined): string | undefined {
  return planHasDiscount && promo?.result === PromoCodeResult.Applied ? promo.code : undefined;
}

/**
 * Whether a sale price preview really took something off the regular price. Paddle's
 * price preview answers with a discount of 0, not an error, once the discount's
 * `expires_at` has passed (sandbox probe, design-docs/promo-campaigns.md), so a sale
 * price that is not below the regular one means Paddle's discount ended before the
 * campaign did. When either amount could not be read there is nothing to compare,
 * and the sale price is trusted.
 */
export function isSalePriceDiscounted(saleMinor: number | undefined, regularMinor: number | undefined): boolean {
  if (saleMinor === undefined || regularMinor === undefined) return true;
  return saleMinor < regularMinor;
}

/**
 * Whether a Paddle checkout error is Paddle refusing the discount, which happens
 * when a page left open shows a sale that has since ended (the discount's
 * `expires_at` has passed), or when the discount no longer fits the price.
 *
 * Probed in the sandbox on 2026-10-29 (design-docs/promo-campaigns.md): Paddle.js
 * sends `checkout.error` with code `validation` and the detail "Discount has
 * expired" or "Discount is not eligible for the provided items". Paddle documents
 * neither text, so the match is on the code plus a detail that starts with
 * "Discount", ignoring case, rather than on the exact sentences.
 */
export function isPromoDiscountRefusal(ev: PaddleEventData): boolean {
  return ev.code === 'validation' && (ev.detail ?? '').toLowerCase().startsWith('discount');
}
